import * as three from './three.bundle.js';
import { LitElement, html, css, nothing } from "./lit-core.min.js";

class Loader {
    constructor() {
        this.queue = [];
        this.receivedLength = 0;
        this.totalLength = 0;
        this.onProgress = null;
    }

    add(url, callback) {
        this.queue.push({ url, callback });
    }

    async load() {
        const responses = await Promise.all(this.queue.map(({ url, callback }) => fetch(url).then(response => ({ response, callback }))));
        
        for (const resp of responses) {
            this.totalLength += parseInt(resp.response.headers.get('content-length'));
        }

        await Promise.all(responses.map(resp => this.readResponse(resp.response, resp.callback)));
    }

    async readResponse(response, callback) {
        let receivedLength = 0;
        let chunks = [];
        const reader = response.body.getReader();

        while(true) {
            const {done, value} = await reader.read();
            
            if (done) {
                break;
            }
            
            chunks.push(value);

            receivedLength += value.length;
            this.receivedLength += value.length;
            if (typeof this.onProgress === 'function') this.onProgress(this.receivedLength, this.totalLength);
        }

        let data = new Uint8Array(receivedLength);
        let position = 0;

        for (const chunk of chunks) {
            data.set(chunk, position);
            position += chunk.length;
        }

        if (callback) await callback(data, response.headers.get('content-type'));
    }
}

class ByobRender extends HTMLElement {
    static get observedAttributes() { return ['render-config']; }

    constructor() {
        super();

        const observer = new IntersectionObserver((entries) => {
            if (entries.some(e => e.isIntersecting)) {
                this.init();
                observer.disconnect();
            }
        });

        this.coloursRequest = fetch('/?view=bundle-colours');

        observer.observe(this);

        // Start loading data when both bundle builder and hero image have loaded
        const bundlePromise = new Promise(resolve => {
            if (window.bundleBuilderReady) resolve();
            else window.addEventListener('bundlebuilderready', resolve);
        });

        const heroPromise = new Promise(resolve => {
            const heroImage = document.querySelector('.bundle-hero__image img');
            if (!heroImage || heroImage.complete) resolve();
            else heroImage.addEventListener('load', resolve);     
        });

        Promise.all([bundlePromise, heroPromise]).then(() => this.loadData());
    }

    async init() {
        const start = performance.now();

        if (this.initialised) return;
        this.initialised = true;
        console.log('Init render');
        three.ColorManagement.legacyMode = false;
        await this.loadData();
        this.setupScene();
        this.setupRenderer();  

        this.loadConfig(this.config);
        this.ready = true;

        window.FS('trackEvent', {
			name: 'BYOB Render Loaded',
			properties: { time: (performance.now() - start) / 1000 }
		});
    }

    setupScene() {
        this.scene = new three.Scene();
        this.scene.position.setY(-0.35);

        // Add main overhead light
        const overheadLight = new three.DirectionalLight(0xedf9ff, 1.6);
        overheadLight.position.set( 2, 3.5, 0.3 );
        overheadLight.castShadow = true;
        overheadLight.shadow.mapSize.width = 256; 
        overheadLight.shadow.mapSize.height = 256; 
        overheadLight.shadow.bias = -0.00002;
        overheadLight.shadow.camera.top = 1;
        overheadLight.shadow.camera.bottom = -1.3; 
        overheadLight.shadow.camera.left = -1.15;
        overheadLight.shadow.camera.right = 1.1;  
        this.scene.add( overheadLight );

        // Add side light
        const sideLight = new three.DirectionalLight(0xedf9ff, 0.4);
        sideLight.position.set( 2, 1, -1 );
        //this.scene.add( sideLight );

        // Add negative light at the front of the scene
        const negativeLight = new three.DirectionalLight(0xffffff, -0.4);
        negativeLight.position.set(-1, -0.5, 0.5);
        //this.scene.add(negativeLight);

         // Add slight positive light at the side of the scene
         const positiveLight = new three.DirectionalLight(0xffffff, 0.4);
         positiveLight.position.set(-1, 0.5, 0.2);
         positiveLight.shadow.bias = -0.00002;
         this.scene.add(positiveLight);

        // Add bed
        this.scene.add(this.bed);

        this.map = this.textures[window.bundleUrls.map];
        this.bumpMap = this.textures[window.bundleUrls.bumpMap];
        let wood = this.textures[window.bundleUrls.wood];

        // Set environment map
        let envMap =  this.textures[window.bundleUrls.envMap];
        envMap.mapping = three.EquirectangularReflectionMapping;
        this.scene.environment = envMap;

        // Set colour maps
        this.maps = {};
        for (let colour of this.bundleColours) {
            if (colour.texture) {
                this.maps[colour.id] = this.textures[colour.texture];
            }
        }

        // Create materials
        this.materials = {};
        this.materialsDoubleSided = {};
        this.materialsQuilt = {};
        this.materialsFrayed = {};

        const frayedTextures = [];

        for (const textureUrl of frayedTextures) {
            this.textures[textureUrl].wrapS = three.RepeatWrapping;
            this.textures[textureUrl].wrapT = three.RepeatWrapping;
            this.textures[textureUrl].repeat.set(1, 1);
            this.textures[textureUrl].flipY = false;
        }

        for (let key in this.colours) {
            let baseColour = new three.Color(this.colours[key]);
            baseColour.name = key;

            // Set default texture map, or use map for specific colour if it exists
            let selectedMap = this.map;
            if (this.maps[key]) selectedMap = this.maps[key];

            this.materials[key] = this.createColourMaterial(baseColour, selectedMap);
            this.materialsDoubleSided[key] = this.createColourMaterial(baseColour, selectedMap, true);
            this.materialsQuilt[key] = this.createColourMaterial(baseColour, selectedMap, false, { normalMap: this.textures[window.bundleUrls.quiltNormalMap] });

            for (const otherKey in this.colours) {
                if (key.includes('stripe') || otherKey.includes('stripe')) continue;

                let otherBaseColour = new three.Color(this.colours[otherKey]);
                let combinedBaseColour = baseColour.clone().lerp(otherBaseColour, 0.5);

                if (frayedTextures.length > 0) {
                    const frayedKey = `${key}-${otherKey}`;

                    this.materialsFrayed[frayedKey] = this.createColourMaterial(combinedBaseColour, selectedMap, false, {
                        normalMap: this.textures[window.bundleUrls.frayedNormal],
                        alphaMap: this.textures[window.bundleUrls.frayedOpacity],
                        bumpMap: null,
                        transparent: true,
                    })
                }
            }
        };

        // Set default material for bedding items
        for (let child of this.bed.children) {
            child.material = this.materials.white;
            child.castShadow = true; //default is false
            child.receiveShadow = true; //default  

            // Scale up flat sheet slightly to avoid z-fighting
            if (child.name.includes('flat-sheet')) {
                child.scale.setScalar(1.01);
            }
        }

        // Set base material
        const baseMat = new three.MeshStandardMaterial({
            map: wood,
            color: new three.Color(0xaa9988),
            envMapIntensity: 1
        });   
        let base = this.scene.getObjectByName("base");
        base.material = baseMat;

        this.decorations = new three.Object3D();
        this.decorations.visible = false;

        // Add bed head
        const bedHeadMat = new three.MeshStandardMaterial({
            map: wood,
            color: new three.Color(0xe0ccb8),
            envMapIntensity: 0.6
        });   
        const geometry = new three.BoxGeometry( 1, 1.5, 0.04 );
        const bedHead = new three.Mesh( geometry, bedHeadMat );
        bedHead.rotateZ(Math.PI / 2);
        bedHead.position.set(0, 0.5, -1.035);
        bedHead.name = 'bed-head';
        bedHead.castShadow = true;
        bedHead.receiveShadow = true;
        this.decorations.add( bedHead );
        this.scene.add(this.decorations);

        // Add shadow
        const shadowPlane = new three.PlaneGeometry( 5, 5 );
        const shadowMat = new three.MeshBasicMaterial({
            color: 0x000000,
            transparent: true,
            opacity: 0.3,
            alphaMap: this.textures[window.bundleUrls.duvetShadowAlpha],
        });
        const shadow = new three.Mesh( shadowPlane, shadowMat );
        shadow.rotateX(-Math.PI / 2);
        this.scene.add( shadow );
        this.shadow = shadow;
        this.shadow.duvetAlpha = this.textures[window.bundleUrls.duvetShadowAlpha];
        this.shadow.quiltAlpha = this.textures[window.bundleUrls.quiltShadowAlpha];
    }

    async loadDecorations() {
        // Add floor
        const textureLoader = new three.TextureLoader();
        const floorTexture = await textureLoader.loadAsync(window.bundleUrls.floorTexture);
        const floorNormal = await textureLoader.loadAsync(window.bundleUrls.floorNormal);
        const floorARM = await textureLoader.loadAsync(window.bundleUrls.floorARM);

        floorTexture.anisotropy = 2;
        floorNormal.anisotropy = 2;
        floorTexture.repeat = new three.Vector2(6, 4); // Will also apply across to all other textures
        floorTexture.wrapS = three.RepeatWrapping;
        floorTexture.wrapT = three.RepeatWrapping;
        floorNormal.wrapS = three.RepeatWrapping;
        floorNormal.wrapT = three.RepeatWrapping;
        floorARM.wrapS = three.RepeatWrapping;
        floorARM.wrapT = three.RepeatWrapping;

        const floorGeometry = new three.PlaneGeometry( 5, 10 );
        const floor = new three.Mesh( floorGeometry );
        const floorMat = new MeshReflectorMaterial(this.renderer, this.camera, this.scene, floor, {
            mirror: 0.3,
            resolution: 512,
            mixContrast: 1.3,
            mixBlur: 2.5,
            mixStrength: 1.75,
            blur: [256, 256],
        }, {
            color: 0x726969,
            normalMap: floorNormal,
            map: floorTexture,
            roughness: 0.5,
            aoMap: floorARM,
        });
        this.floorMaterial = floorMat;
        floor.position.setZ(1.4);
        floor.material = floorMat;
        floor.rotateX(-Math.PI / 2);
        floor.rotateZ(Math.PI / 2);
        //floor.position.setY(-0.35);
        floor.receiveShadow = false;
        this.decorations.add( floor );

        const fadeGeometry = new three.PlaneGeometry( 7, 10 );
        const fadeAlpha = textureLoader.load(window.bundleUrls.floorAlpha);
        const fadeMat = new three.MeshStandardMaterial({ color: 0xffffff, alphaMap: fadeAlpha, transparent: true, envMapIntensity: 3 });
        const fade = new three.Mesh( fadeGeometry, fadeMat );
        fade.position.setZ(2.45);
        fade.rotateX(-Math.PI / 2);
        fade.rotateZ(Math.PI / 2);
        fade.position.setY(0.01);
        this.decorations.add( fade );

        const wallFade = new three.Mesh( fadeGeometry, fadeMat );
        wallFade.material = new three.MeshStandardMaterial({ color: 0xffffff, alphaMap: fadeAlpha, transparent: true, envMapIntensity: 4 });
        wallFade.position.setZ(-1.05);
        wallFade.position.setY(3.5);
        wallFade.rotateZ(-Math.PI / 2);
        this.decorations.add( wallFade );
        
        const wallGeometry = new three.PlaneGeometry( 10, 6 );
        const wall = new three.Mesh( wallGeometry );
        wall.material = new three.MeshStandardMaterial({ color: 0xffffff });
        wall.position.setZ(-1.11);
        wall.position.setY(3);
        wall.receiveShadow = true;
        wall.material.envMapIntensity = 3;
        this.decorations.add( wall );

        const skirtingGeometry = new three.PlaneGeometry( 10, 0.015 );
        const skirting = new three.Mesh( skirtingGeometry );
        skirting.material = new three.MeshStandardMaterial({ color: 0xffffff, envMapIntensity: 5 });
        this.decorations.add( skirting );
        skirting.position.setZ(-1.07);
        skirting.position.setY(0.11);
        skirting.rotateX(-Math.PI / 4);

        const skirtingFrontGeometry = new three.PlaneGeometry( 10, 0.1 );
        const skirtingFront = new three.Mesh( skirtingFrontGeometry );
        skirtingFront.material = new three.MeshStandardMaterial({ color: 0xffffff, envMapIntensity: 3.5 });
        this.decorations.add( skirtingFront );
        skirtingFront.position.setZ(-1.06);
        skirtingFront.position.setY(0.05);


        const ceiling = new three.Mesh( floorGeometry );
        ceiling.material = new three.MeshBasicMaterial({ color: 0x888888 });
        ceiling.position.setY(2.5);
        ceiling.position.setZ(1.4);
        ceiling.rotateX(Math.PI / 2);
        ceiling.rotateZ(Math.PI / 2);
        ceiling.receiveShadow = false;
        //this.scene.add( ceiling );

        this.dispatchEvent(new CustomEvent('decorationsloaded'));
    }

    async loadData() {
        if (this.dataPromise) return this.dataPromise;
        console.log('Load data');
        this.dataPromise = this.fetchData();
        return this.dataPromise;
    }

    async fetchData() {
        // Fetch colours
        const coloursResp = await this.coloursRequest;
        this.bundleColours = await coloursResp.json();
        this.colours = Object.fromEntries(this.bundleColours.map(c => [c.id, c.hex]));

        // Load textures
        const textureLoader = new three.TextureLoader();

        const textureUrls = [
            window.bundleUrls.map,
            window.bundleUrls.bumpMap,
            window.bundleUrls.wood,
            window.bundleUrls.envMap,
            window.bundleUrls.quiltNormalMap,
            window.bundleUrls.duvetShadowAlpha,
            window.bundleUrls.quiltShadowAlpha,
            ...this.bundleColours.filter(colour => colour.texture).map(colour => colour.texture)
        ];

        const urlLoader = new Loader();
        urlLoader.onProgress = (received, total) => this.style.setProperty('--progress', (received / total).toFixed(2));

        this.textures = {};

        for (const url of textureUrls) {
            urlLoader.add(url, (data, type) => {
                const blob = new Blob([data], { type });
                const dataUrl = URL.createObjectURL(blob);
                this.textures[url] = textureLoader.load(dataUrl);
            });
        }

        // Load model
        const loader = new three.GLTFLoader();
        const dracoLoader = new three.DRACOLoader();
        dracoLoader.setDecoderPath('https://www.gstatic.com/draco/versioned/decoders/1.5.6/');
        loader.setDRACOLoader(dracoLoader);

        urlLoader.add(window.bundleUrls.model, async (data, type) => {
            let model = await loader.parseAsync(data.buffer);
            this.bed = model.scene;
        });

        this.classList.add('loading');
        await urlLoader.load();
        this.classList.add('loaded'); 
    }

    getMaterialColours(baseColour) {
        let { h, s, l } = baseColour.getHSL({});

        let emissiveBase = l + 0.1;
        let emissiveAmount = emissiveBase * emissiveBase * 0.2;
        let emissive = baseColour.clone().multiplyScalar(emissiveAmount);
        let colour = baseColour.clone().multiplyScalar(1 - emissiveAmount * 0.8);
        return { colour, emissive };
    }

    createColourMaterial(baseColour, map, doubleSided = false, props = {}) {
        let { colour, emissive } = this.getMaterialColours(baseColour);
            
        let materialConfig = {
            bumpScale: .025,
            map: map,
            bumpMap: this.bumpMap,
            roughness: 1,
            metalness: 0,
            color: colour,
            emissive: emissive,
            envMapIntensity: 0.45,
            side: doubleSided ? three.DoubleSide : three.FrontSide,
            normalScale: new three.Vector2(2, 2),
            ...props
        };

        return new three.MeshStandardMaterial(materialConfig);
    }

    setupRenderer() {
        this.renderer = new three.WebGLRenderer({antialias: true, preserveDrawingBuffer: true});
        this.renderer.outputEncoding = three.sRGBEncoding;
        this.renderer.toneMapping = three.ACESFilmicToneMapping;
        this.renderer.shadowMap.autoUpdate = false;
        this.renderer.shadowMap.needsUpdate = true;
        this.renderer.shadowMap.type = three.PCFSoftShadowMap;
        this.renderer.setClearColor(new three.Color(0xfafafa), 1 );
        this.renderer.toneMappingExposure = 0.9;
        
        this.renderer.shadowMap.enabled = true;

        let root = this.attachShadow({mode: 'closed'});
        root.appendChild(this.renderer.domElement);
        this.renderer.domElement.style.opacity = '0';

        this.renderer.domElement.insertAdjacentHTML('beforebegin', /* html */`
            <style>
                canvas {
                    aspect-ratio: 1;
                    max-width: 100%;
                    max-height: var(--max-height, 100%);
                    width: 100%;
                    height: 100%;
                    object-fit: contain;
                    transform: scale(var(--zoom, 1));
                    transition: transform 300ms, opacity 350ms;
                }
            </style>
        `);

        let camera = new three.PerspectiveCamera( 30, 1.5, 0.1, 1000 );
        camera.position.z = 4;
        camera.position.y = 1.75;
        this.camera = camera;

        // Setup controls - allows rotation and zoom using mouse or touch
        const controls = new three.OrbitControls(camera, this.renderer.domElement);
        controls.autoRotate = this.dataset.colourPicker !== 'true';
        controls.autoRotateSpeed = 0.31;
        controls.enableZoom = true;
        controls.enableDamping = true;
        controls.enablePan = false;
        controls.minDistance = 2.5;
        controls.maxDistance = Math.sqrt(Math.pow(4, 2) + Math.pow(1.75, 2)); // Restrict to default distance
        controls.maxPolarAngle = Math.PI / 2;
        controls.minAzimuthAngle = -Math.PI / 2;
        controls.maxAzimuthAngle = Math.PI / 2;
        this.controls = controls;

        let clickStartX = null;
        let clickStartY = null;
        let animHandle = null;

        this.setAttribute('paused', false);
    
        this.addEventListener('mousedown', e => {
            clickStartX = e.offsetX;
            clickStartY = e.offsetY;
        })

        this.addEventListener('mouseup', e => {
            if (this.dataset.colourPicker === 'true') return;
            if (clickStartX == null || clickStartY == null) return; 
            const dist = Math.sqrt(Math.pow(e.offsetX - clickStartX, 2) + Math.pow(e.offsetY - clickStartY, 2));
            if (dist < 1) {
                if (animHandle != null) clearTimeout(animHandle);
                this.classList.remove('pause-animation');
                controls.autoRotate = !controls.autoRotate;
                this.setAttribute('paused', !controls.autoRotate);
                this.classList.add('pause-animation');
                animHandle = setTimeout(() => this.classList.remove('pause-animation'), 450);
            }
            clickStartX = null;
            clickStartY = null;
        });

        const playAnimOnLoad = () => {
            if (document.documentElement.classList.contains('modal-open') || !document.body.classList.contains('bundle-started')) setTimeout(playAnimOnLoad, 100);
            else {
                setTimeout(() => {
                    if (animHandle != null) clearTimeout(animHandle);
                    this.classList.add('pause-animation');
                    animHandle = setTimeout(() => this.classList.remove('pause-animation'), 450);
                }, 100);   
            }
        }

        playAnimOnLoad();

        // Add colour picker if enabled, and disable rotation
        if (this.dataset.colourPicker === "true") {
            const picker = `
            <style>
            .colour-picker {
                margin-bottom: 8px;
                display: flex;
                gap: 8px;
                z-index: 5;
                padding: 8px;
                background: white;
            }

            .colour-picker label {
                font-weight: 400;
            }
            </style>
            <div class="colour-picker">
                <label>Colour</label>
                <input type="color" value="${this.colours.white}">
                <input type="text" value="${this.colours.white}">
                <label for="stripe">Stripe</label>
                <select name="stripe" id="stripe">
                    <option value="none">None/Custom</option>
                    <option value="stripe">Stripe</option>
                    <option value="pinstripe">Pinstripe</option>
                    <option value="olive-stripe">Olive Stripe</option>
                    <option value="sage-olive-stripe">Sage & Olive Stripe</option>
                </select>
                <input type="file" name="texture" id="texture" accepts="image/jpeg, image/png">
            </div>
            `;
            controls.autoRotate = false;
            root.querySelector('canvas').insertAdjacentHTML('beforebegin', picker);
            this.style.setProperty('--zoom', 1.5);
            let colour = this.colours.white;
            let stripe = 'none';

            root.querySelector('.colour-picker input[type="color"]').addEventListener('input', e => {
                colour = e.currentTarget.value;
                this.forceColour(colour, stripe);
                root.querySelector('.colour-picker input[type="text"]').value = e.currentTarget.value;
            });

            root.querySelector('.colour-picker input[type="color"]').addEventListener('change', e => {
                colour = e.currentTarget.value;
                this.forceColour(colour, stripe);
                root.querySelector('.colour-picker input[type="text"]').value = e.currentTarget.value;
            });

            root.querySelector('.colour-picker input[type="text"]').addEventListener('input', e => {
                colour = e.currentTarget.value;
                this.forceColour(colour, stripe);
                root.querySelector('.colour-picker input[type="color"]').value = e.currentTarget.value;
            });

            root.querySelector('.colour-picker select').addEventListener('change', e => {
                stripe = e.currentTarget.value;
                this.forceColour(colour, stripe);
            });
            
            root.querySelector('.colour-picker input[type="file"]').addEventListener('change', e => {
                let file = e.currentTarget.files[0];
                let reader = new FileReader(file);
                reader.addEventListener('load', e => {
                    let texture = new three.TextureLoader().load(e.target.result) 
                    texture.repeat = new three.Vector2(0.5, 0.5);
                    stripe = file.name;
                    this.maps[file.name] = texture;
                    this.forceColour(colour, stripe);
                })
                reader.readAsDataURL(file);
            });
        }

        // Add render controls
        if (window.featureEnabled && window.featureEnabled('bundle-decorations')) {
            const renderControls = document.createElement('byob-render-controls');
            renderControls.byobRender = this;
            root.appendChild(renderControls);
        }

        // Add progressive shadowmap
        let progressiveShadows = new ProgressiveShadows(this.renderer, this.scene, { size: 5, lightRadius: 4, frames: 120, lightCount: 8, shadowMapRes: 1024, ambientWeight: 0.6 });
        progressiveShadows.shadowCatcherMaterial.opacity = 0.4;
        progressiveShadows.lightOrigin.position.set(3.5,3.5,0);
        progressiveShadows.ambientWeight = 0.5;
        this.progressiveShadows = progressiveShadows;
        this.progressiveShadows.progShadowGrp.visible = false;

        // Update render size when element size changes
        const updateRenderSize = () => {
            const rect = this.renderer.domElement.getBoundingClientRect();
            const size = Math.min(rect.width, rect.height);
            let ratio = Math.min(window.devicePixelRatio, 1.5);
            this.renderer.setSize(size * ratio, size * ratio, false);
            camera.aspect = 1;
            camera.zoom = 0.6;
            camera.updateProjectionMatrix();
        };
        this.updateRenderSize = updateRenderSize;

        // Improve resize performance by waiting until resizing is finished
        let handler = null;
        const observer = new ResizeObserver(() => {
            if (handler) clearTimeout(handler);
            handler = setTimeout(() => {
                updateRenderSize();
                clearTimeout(handler);
            }, 100);
        });
        observer.observe(this);

        updateRenderSize();

        if (window.featureEnabled && window.featureEnabled('bundle-decorations')) this.loadDecorations();

        // Setup update loop
        const animate = () => {
            requestAnimationFrame(animate); 
            controls.update(); 
            if (controls.getAzimuthalAngle() >= controls.maxAzimuthAngle - 0.05) controls.autoRotateSpeed = Math.abs(controls.autoRotateSpeed);
            if (controls.getAzimuthalAngle() <= controls.minAzimuthAngle + 0.05) controls.autoRotateSpeed = -Math.abs(controls.autoRotateSpeed);

            if (this.decorations && this.decorations.visible) this.floorMaterial.update();
            this.renderer.render(this.scene, camera);
            
            if (this.renderer.domElement.style.opacity == '0') {
                this.renderer.domElement.style.opacity = '1';
            }
        };

        animate();
    }

    createScreenshot(width = 400, height = undefined, { config = null, enableDecorations = false, cameraDist = 1 } = {}) {
        let aspect = width / (height ? height : width * 1.25);

        let camera = new three.PerspectiveCamera( 30, aspect, 0.1, 1000 );
        camera.position.z = 5 * cameraDist;
        camera.position.y = 2.5 * cameraDist;
        camera.lookAt(new three.Vector3(0, 0, 0));

        const decorationsVisible = this.decorations.visible;
        this.decorations.visible = enableDecorations;

        if (config) this.loadConfig(config,  { updateShadows: false, fireEvent: false });

        let origSize = this.renderer.getSize(new three.Vector2());
        this.renderer.setSize(width, height ? height : width * 1.25, false);
        camera.updateProjectionMatrix();

        if (this.floorMaterial) {
            this.floorMaterial.camera = camera;
            this.floorMaterial.update();
        }

        this.renderer.render(this.scene, camera);
        let canvas = this.renderer.domElement;
        let image = canvas.toDataURL('image/jpeg', 80);

        if (this.floorMaterial) this.floorMaterial.camera = this.camera;

        this.renderer.setSize(origSize.width, origSize.height, false);
        if (config) this.loadConfig(this.config, { updateShadows: false, fireEvent: false });
        this.decorations.visible = decorationsVisible;

        return image;
    }

    updateConfig(config, { updateShadows = true, fireEvent = true } = {}) {
        let selections = Object.values(config).map(v => [v.fabricSection, v.selectedOption, v.flipped]);
        let sections = selections.map(s => s[0]);
        const firstCover = sections.find(s => s.includes('duvet') || s.includes('quilt'));
        const coverType = firstCover.includes('quilt') ? 'quilt' : 'duvet';

        for (let child of this.bed.children) {
            if (child.name != 'base' && child.name != 'bed-head') child.visible = false;
        }

        // Add spacing when scalloped standards are present
        const hasScallopedStandard = selections.some(s => s[0].includes('standard') && s[1].includes('scalloped'));
        const standards = this.bed.children.filter(c => c.name.includes('standard'));

        if (hasScallopedStandard) {
            for (const standard of standards) {
                if (!standard._scallopedTransform || standard._scallopedTransform == 0) {
                    standard._scallopedTransform = standard.name.endsWith('a') ? -0.03 : 0.03;
                    standard.translateX(standard._scallopedTransform);
                }
            }
        }
        else {
            for (const standard of standards) {
                if (standard._scallopedTransform) {
                    standard.translateX(-standard._scallopedTransform);
                    standard._scallopedTransform = 0;
                }
            }
        }

        // Vertical adjustment when there only a single standard pillow
        const standardSelections = selections.filter(s => s[0].includes('standard'));
        const pillowSelections = selections.filter(s => s[0].includes('pillowcase'));

        if (standardSelections.length == 1 && pillowSelections.length == 1) {
            for (const standard of standards) {
                if (!standard._singleStandardTransform || standard._singleStandardTransform == 0) {
                    standard._singleStandardTransform = 0.12;
                    standard.translateY(standard._singleStandardTransform);
                }
            }
        }
        else {
            for (const standard of standards) {
                if (standard._singleStandardTransform) {
                    standard.translateY(-standard._singleStandardTransform);
                    standard._singleStandardTransform = 0;
                }
            }
        }

        for (let [section, selection, flipped] of selections) {
            if (section == 'flat-sheet_1_1') section = `flat-sheet_1_1_${coverType}`
            if (section == 'flat-sheet_1_2') section = `flat-sheet_1_2_${coverType}`

            if (section == 'quilt') {
                let front = this.scene.getObjectByName('quilt_front');
                let back = this.scene.getObjectByName('quilt_back');

                front.visible = true;
                back.visible = true;

                if (selection.includes('-')) {
                    let split = selection.split('-');
                    let backSelection = split.pop();
                    let frontSelection = split.join('-');

                    front.material = this.materialsQuilt[flipped ? backSelection : frontSelection];
                    back.material = this.materialsQuilt[flipped ? frontSelection : backSelection];
                }
                else {
                    front.material = this.materialsQuilt[selection];
                    back.material = this.materials[selection];
                }
            }
            else if (section.includes('throw')) {
                const top = this.scene.getObjectByName(`throw-top_${coverType}`);
                const bottom = this.scene.getObjectByName(`throw-bottom_${coverType}`);

                top.visible = true;
                bottom.visible = true;

                top.material = this.materials[selection];
                bottom.material = this.materials[selection];

                if (selection.includes('-')) {
                    let split = selection.split('-');
                    let bottomSelection = split.pop();
                    let topSelection = split.join('-');

                    top.material = this.materials[flipped ? topSelection : bottomSelection];
                    bottom.material = this.materials[flipped ? bottomSelection : topSelection];
                }

                if (section.includes('muslin')) {
                    const topEdge = this.scene.getObjectByName(`throw-top-edge_${coverType}`);
                    const bottomEdge = this.scene.getObjectByName(`throw-bottom-edge_${coverType}`);
                   
                    topEdge.visible = true;
                    bottomEdge.visible = true;

                    topEdge.material = this.materialsFrayed[`${selection}-${selection}`];
                    bottomEdge.material = this.materialsFrayed[`${selection}-${selection}`];

                    if (selection.includes('-')) {
                        let split = selection.split('-');
                        let bottomSelection = split.pop();
                        let topSelection = split.join('-');

                        topEdge.material = this.materialsFrayed[`${topSelection}-${bottomSelection}`];
                        bottomEdge.material = this.materialsFrayed[`${topSelection}-${bottomSelection}`];
                    }
                }
            }
            else {
                const suffixes = section.includes('standard') ? ['a', 'b'] : [''];
                if (section == 'standard-pillowcase_1_1_0') section = `standard-pillowcase_1_2_0`;

                for (const suffix of suffixes) {
                    const objectId = `${section}${suffix}`;
                    let child = this.scene.getObjectByName(objectId);
                    if (!child) {
                        console.warn(`Could not find object with name ${objectId}`);
                        continue;
                    }
                    child.visible = true;

                    if (selection.includes('scalloped')) {
                        const type = objectId.includes('euro') ? 'euro' : 'standard';

                        let split = selection.split('-scalloped')[0].split('-');
                        let outerSelection = split.pop();
                        let innerSelection = split.join('-');
                        let id = objectId.split('_').slice(1).join('_');

                        let inner = this.scene.getObjectByName(`scalloped-${type}-inner_${id}`);
                        let outer = this.scene.getObjectByName(`scalloped-${type}-outer_${id}`);

                        inner.visible = true;
                        outer.visible = true;
                        if (this.materials[innerSelection]) {
                            child.material = this.materials[innerSelection];
                            inner.material = this.materialsDoubleSided[innerSelection];
                        }
                        if (this.materials[outerSelection]) outer.material = this.materialsDoubleSided[outerSelection];
                    }
                    else {
                        if (this.materials[selection]) child.material = this.materials[selection];
                    }
                }
            }
        }

        this.renderer.shadowMap.needsUpdate = true;
        this.prevSections = sections;

        // Dispatch render update event
        if (this.renderer && this.dataset.events == 'true' && fireEvent) {
            setTimeout(() => {
                this.dispatchEvent(new CustomEvent('renderupdate', { 
                    detail: { 
                        image: this.createScreenshot(1000), 
                        thumb: this.createScreenshot(400) 
                    }, 
                    bubbles: true })
                );
            }, 100);
        }
    }

    forceColour(hex, stripe = 'none') {
        let baseColour = new three.Color(hex);
        let map = this.map;

        if (stripe && stripe !== 'none') map = this.maps[stripe];

        if (this.forceMaterial == undefined) {
            this.forceMaterial = this.createColourMaterial(baseColour, map);
        }

        let { colour, emissive } = this.getMaterialColours(baseColour);
        this.forceMaterial.color = colour;
        this.forceMaterial.emissive = emissive;
        this.forceMaterial.map = map;

        for (let child of this.bed.children) {
            if (child.name == 'base') continue;
            child.material = this.forceMaterial;
        }
    }

    loadConfig(config,  { updateShadows = true, fireEvent = true } = {}) {
        const handlise = (string) => {
            return string.toLowerCase().normalize("NFD").replace(/\p{Diacritic}/gu, "").replace(/[^a-z0-9]+/g, '-');
        }

        let variants = config.variants;
        variants = variants.map(v => ({ type: handlise(v.type), colour: handlise(v.colour), flipped: v.flipped }));

        const types = {};
        
        // No king pillow model, so replace any with standard
        for (const variant of variants) {
            if (variant.type == 'king-pillowcase') variant.type = 'standard-pillowcase';
        }
        
        // Group variants by type, and find index of each variant (within selected variants of same type)
        for (const variant of variants) {
            if (!types[variant.type]) types[variant.type] = variants.filter(v => v.type == variant.type);
            variant.index = types[variant.type].indexOf(variant) + 1;
        }

        // Set model IDs
        for (const variant of variants) {
            if (variant.type == 'throw') variant.modelId = 'throw';
            else if (variant.type == 'muslin-throw') variant.modelId = 'muslin-throw';
            else if (variant.type == 'quilt') {
                variant.modelId = `quilt`;
            }

            // For standard pillows, format is "<type>_<index of variant within type>_<total standard>_<total european>"
            else if (variant.type.includes('pillow') && !variant.type.includes('european')) {
                variant.modelId = `${variant.type}_${variant.index}_${types[variant.type].length}_${types['european-pillowcase'] ? types['european-pillowcase'].length : 0}`;
            }

            // For other products, format is "<type>_<index of variant within type>_<total of type>"
            else variant.modelId = `${variant.type}_${variant.index}_${types[variant.type].length}`;
        }

        const renderConfig = Object.fromEntries(variants.map((variant, idx) => [
            idx, { fabricSection: variant.modelId, selectedOption: variant.colour, flipped: variant.flipped }
        ]));

        this.updateConfig(renderConfig, { updateShadows, fireEvent });

        // Set floor shadow map
        this.shadow.material.alphaMap = variants[0].type == 'quilt' ? this.shadow.quiltAlpha : this.shadow.duvetAlpha;
    }

    attributeChangedCallback(name, oldValue, newValue) {
        if (name == 'render-config' && newValue && newValue != oldValue) {
            this.config = JSON.parse(newValue);
            if (this.ready) {
                if (this.updateHandle) cancelAnimationFrame(this.updateHandle);
                this.updateHandle = requestAnimationFrame(() => this.loadConfig(this.config));
            }
        }
    }

    generateShadowTexture() {
        let ps = this.progressiveShadows;
        let canvas
        let uint8ClampedArray
        let color;

        ps.clear();
        ps.progShadowGrp.visible = true;

        this.scene.position.setY(0);
        ps.update(this.camera);
        this.scene.position.setY(-0.35);
        this.renderer.shadowMap.needsUpdate = true;

        ps.progShadowGrp.visible = false;

        if (!canvas) {
            canvas = document.createElement("canvas")
            color = new Color()
        }

        const renderTarget = ps.progressiveLightMap1
        const width = renderTarget.width
        const height = renderTarget.height
        console.log(renderTarget)
        const pixels = new Float32Array(width * height * 4)
        ps.renderer.readRenderTargetPixels(renderTarget, 0, 0, width, height, pixels)
        const shadowColor = ps.shadowCatcherMaterial.color
        const blend = ps.shadowCatcherMaterial.blend

        let min = 100000,
            max = 0
        for (let i = 0; i < pixels.length; i += 4) {
            min = Math.min(min, pixels[i])
            max = Math.max(max, pixels[i])
        }

        const range = max - min
        const alphaScale = 1 / range

        for (let i = 0; i < pixels.length; i += 4) {
            color.fromArray(pixels, i)
            const diffuse = color.r
            const invertedValue = max - diffuse
            const alphaValue = invertedValue * alphaScale
            pixels[i + 3] = 255;
            color.setRGB(255 * (alphaValue / 255), 255 * (alphaValue / 255), 255 * (alphaValue / 255))
            // color.convertLinearToSRGB()
            color.toArray(pixels, i)
        }

        if (!uint8ClampedArray || uint8ClampedArray.length !== pixels.length) {
            uint8ClampedArray = new Uint8ClampedArray(pixels.length)
        }

        // Flip pixels vertically
        for (let y = 0; y < height / 2; y++) {
            for (let x = 0; x < width; x++) {
                const i = (y * width + x) * 4
                const j = ((height - y - 1) * width + x) * 4
                for (let k = 0; k < 4; k++) {
                    const temp = pixels[i + k]
                    pixels[i + k] = pixels[j + k]
                    pixels[j + k] = temp
                }
            }
        }

        for (let i = 0; i < pixels.length; i++) {
            uint8ClampedArray[i] = Math.round(pixels[i] * 255)
        }

        // setup canvas to draw the image data onto
        canvas.width = width
        canvas.height = height

        // Draw the image data onto the canvas
        const context = canvas.getContext("2d")
        const imageData = new ImageData(uint8ClampedArray, width, height)
        context.putImageData(imageData, 0, 0)

        // Create a data URL for the image
        const pngUrl = canvas.toDataURL("image/jpeg", 90);

        // Create a new anchor element
        const link = document.createElement("a")

        // Set the href attribute of the anchor element to the PNG data URL
        link.href = pngUrl

        // Set the download attribute of the anchor element to the desired file name
        const selectedType = this.config.variants[0].type.split(' ')[0].toLowerCase();
        link.download = `${selectedType}-shadow-alpha.jpg`;

        // Simulate a click on the anchor element to download the image
        link.click();
    }
}

customElements.define('byob-render', ByobRender);

class ByobRenderControls extends LitElement {
    static properties = {
        byobRender: { type: Object },
        screenshot: { type: String },
        scaleAmount: { type: Number },
        decorationsLoaded: { type: Boolean },
    }

    constructor() {
        super();
        this.decorationsLoaded = false;
    }

    updateScreenshot() {
        this.screenshot = this.byobRender.createScreenshot(128, 128, { enableDecorations: !this.byobRender.decorations.visible });
    }

    connectedCallback() {
        super.connectedCallback();

        this.byobRender.addEventListener('decorationsloaded', e => {
            this.decorationsLoaded = true;
            this.updateScreenshot();
            this.byobRender.addEventListener('renderupdate', e => this.updateScreenshot());
        });

        // Stop from triggering render pause
        this.addEventListener('mouseup', e => {
            e.preventDefault();
            e.stopPropagation();
        })
    }

    toggleDecorations(e) {
        e.preventDefault();
        e.stopPropagation();
        this.byobRender.decorations.visible = !this.byobRender.decorations.visible;
        if (window.trackEvent) window.trackEvent('BYOB', this.byobRender.decorations.visible ? 'Show-decorations' : 'Hide-decorations');
        this.updateScreenshot();
    }

    render() {
        return this.decorationsLoaded ? html`
            <button aria-label="Change style" @click=${this.toggleDecorations}>
                <img src="${this.screenshot}">
            </button>
        ` : nothing;
    }

    static styles = css`
        :host {
            position: absolute;
            top: 0;
            left: 0;
            bottom: 0;
            right: 0;
            pointer-events: none;
        }

        @keyframes fadeIn {
            0% {
                opacity: 0;
            }
            100% {
                opacity: 1;
            }
        }

        button {
            width: 64px;
            height: 64px;
            padding: 0;
            border: none;
            background: lightgray;
            border-radius: var(--radious-radius-md, 8px);
            overflow: hidden;
            position: absolute;
            bottom: 8px;
            right: 8px;
            box-shadow: 0px 1px 2px 0px rgba(0, 0, 0, 0.14);
            pointer-events: all;
            transform-origin: bottom right;
            cursor: pointer;
            animation: fadeIn 350ms both;
        }

        button[disabled] {
            pointer-events: none;
        }

        @media (hover: hover) {
            button:hover {
                filter: brightness(0.95);
            }
        }

        button:active {
            filter: brightness(0.9);
        }

        img {
            width: 100%;
            height: 100%;
            object-fit: cover;
            object-position: center;
        }

        @media screen and (min-width: 992px) {
            button {
                width: 80px;
                height: 80px;
            }
        }
    `;
}

customElements.define('byob-render-controls', ByobRenderControls);


// ProgressiveShadows.js
// Taken from https://github.com/vis-prime/progressive-shadows
// Apache-2.0 License
//
// Changes made:
// - Optimized for performing entire calculation in a single frame, instead of across multiple frames
// - Use pseudo-random seeded RNG to generate light positions, to elimate appearance changes after recalculation

import {
    Color,
    DirectionalLightHelper,
    Plane,
    PlaneHelper,
    Vector3,
    MeshLambertMaterial,
    DirectionalLight,
    DoubleSide,
    FloatType,
    Group,
    HalfFloatType,
    Mesh,
    MeshBasicMaterial,
    PlaneGeometry,
    WebGLRenderTarget,
    ShaderMaterial,
    MathUtils,
    UniformsUtils,
    Camera,
} from "./three.bundle.js"

class ProgressiveShadows {
    constructor(
        renderer,
        scene,
        {
            resolution = 1024,
            shadowMapRes = 512,
            shadowBias = 0.001,
            lightCount = 8,
            size = 4,
            frames = 40,
            lightRadius = 2,
            ambientWeight = 0.5,
            alphaTest = 0.98,
        } = {}
    ) {
        this.params = {
            enabled: true,
            frames,
            lightRadius,
            ambientWeight,
            alphaTest,
            debugHelpers: false,
            size,
        }
        this.scene = scene
        this.renderer = renderer
        this.buffer1Active = false
        this.dirLights = []
        this.dirLightsHelpers = []
        this.clearColor = new Color()
        this.clearAlpha = 0
        this.progress = 0
        this.discardMaterial = new DiscardMaterial()
        this.lights = []
        this.meshes = []
        this.objectsToHide = []
        this.framesDone = 0

        // All objects3d made by this class is added here , which is added to the scene
        this.progShadowGrp = new Group()
        this.progShadowGrp.name = "progressive_shadow_assets"
        this.progShadowGrp.renderOrder = -1;
        this.scene.add(this.progShadowGrp)

        //light position control (only the position of this object used , lights are added to light group)
        this.lightOrigin = new Group()
        this.lightOrigin.name = "light_origin"
        this.lightOrigin.position.set(size, size, size)
        this.progShadowGrp.add(this.lightOrigin)

        // All lights are added to this group
        this.lightGroup = new Group()
        this.lightGroup.name = "all_dir_lights"
        this.progShadowGrp.add(this.lightGroup)

        // create 8 directional lights to speed up the convergence
        for (let l = 0; l < lightCount; l++) {
            const dirLight = new DirectionalLight(0xffffff, 1 / lightCount)
            dirLight.name = "dir_light_" + l
            dirLight.castShadow = true
            dirLight.shadow.bias = shadowBias
            dirLight.shadow.camera.near = 0.1
            dirLight.shadow.camera.far = 50
            dirLight.shadow.camera.right = size / 2
            dirLight.shadow.camera.left = -size / 2
            dirLight.shadow.camera.top = size / 2
            dirLight.shadow.camera.bottom = -size / 2
            dirLight.shadow.mapSize.width = shadowMapRes
            dirLight.shadow.mapSize.height = shadowMapRes
            this.dirLights.push(dirLight)
            this.lightGroup.add(dirLight)
        }

        // Create the Progressive LightMap Texture
        const format = /(Android|iPad|iPhone|iPod)/g.test(navigator.userAgent) ? HalfFloatType : FloatType
        this.progressiveLightMap1 = new WebGLRenderTarget(resolution, resolution, { type: format, encoding: this.renderer.outputEncoding })
        this.progressiveLightMap2 = new WebGLRenderTarget(resolution, resolution, { type: format, encoding: this.renderer.outputEncoding })

        // Material applied to shadow catching plane
        this.shadowCatcherMaterial = new SoftShadowMaterial({
            map: this.progressiveLightMap2.texture,
        })

        // Create plane to catch shadows
        this.shadowCatcherMesh = new Mesh(new PlaneGeometry(size, size).rotateX(-Math.PI / 2), this.shadowCatcherMaterial)
        this.shadowCatcherMesh.position.y = 0.001 // avoid z-flicker
        this.shadowCatcherMesh.name = "shadowCatcherMesh"
        this.shadowCatcherMesh.receiveShadow = true
        this.progShadowGrp.add(this.shadowCatcherMesh)

        // Create group to add assets to debug shadow catcher
        this.debugHelpersGroup = new Group()

        //Inject some spicy new logic into a standard Lambert material
        this.targetMat = new MeshLambertMaterial({ fog: false })
        this.previousShadowMap = { value: this.progressiveLightMap1.texture }
        this.averagingWindow = { value: frames }
        this.targetMat.onBeforeCompile = (shader) => {
            // Vertex Shader: Set Vertex Positions to the Unwrapped UV Positions
            shader.vertexShader =
                "varying vec2 vUv;\n" + shader.vertexShader.slice(0, -1) + "vUv = uv; gl_Position = vec4((uv - 0.5) * 2.0, 1.0, 1.0); }"

            // Fragment Shader: Set Pixels to average in the Previous frame's Shadows
            const bodyStart = shader.fragmentShader.indexOf("void main() {")
            shader.fragmentShader =
                "varying vec2 vUv;\n" +
                shader.fragmentShader.slice(0, bodyStart) +
                "uniform sampler2D previousShadowMap;\n	uniform float averagingWindow;\n" +
                shader.fragmentShader.slice(bodyStart - 1, -1) +
                `\nvec3 texelOld = texture2D(previousShadowMap, vUv).rgb;
        gl_FragColor.rgb = mix(texelOld, gl_FragColor.rgb, 1.0/ averagingWindow);
    }`

            // Set the Previous Frame's Texture Buffer and Averaging Window
            shader.uniforms.previousShadowMap = this.previousShadowMap
            shader.uniforms.averagingWindow = this.averagingWindow
        }
    }

    /**
     * This function renders each mesh one at a time into their respective surface maps
     * @param {Camera} camera Standard Rendering Camera
     */
    renderOnRenderTargets(camera) {
        // this.targetMat.uniforms.averagingWindow = { value: this.params.blendWindow }
        this.averagingWindow.value = this.params.frames

        // Ping-pong two surface buffers for reading/writing
        const activeMap = this.buffer1Active ? this.progressiveLightMap1 : this.progressiveLightMap2
        const inactiveMap = this.buffer1Active ? this.progressiveLightMap2 : this.progressiveLightMap1

        // Render the object's surface maps
        this.renderer.setRenderTarget(activeMap)
        // this.targetMat.uniforms.previousShadowMap = { value: inactiveMap.texture }
        this.previousShadowMap.value = inactiveMap.texture

        this.buffer1Active = !this.buffer1Active
        this.renderer.shadowMap.needsUpdate = true;
        this.renderer.render(this.scene, camera);
    }

    /**
     * Make Debug assets visible
     * @param {boolean} visible Whether the debug plane should be visible
     */
    showDebugHelpers(visible) {
        if (!this.debugHelpersGroup.children.length) {
            const renderTargetDisplayHelper = new Mesh(
                new PlaneGeometry(2, 2),
                new MeshBasicMaterial({
                    map: this.progressiveLightMap1.texture,
                    side: DoubleSide,
                })
            )
            renderTargetDisplayHelper.position.y = this.params.size / 2

            for (const dirLight of this.dirLights) {
                const helpers = new DirectionalLightHelper(dirLight)
                this.dirLightsHelpers.push(helpers)
            }

            const shadowAreaHelper = new PlaneHelper(new Plane(new Vector3(0, 1, 0), 0.001), this.params.size, 0xffff00)
            this.debugHelpersGroup.add(renderTargetDisplayHelper, shadowAreaHelper, ...this.dirLightsHelpers)
        }

        if (visible) {
            this.progShadowGrp.add(this.debugHelpersGroup)
            this.dirLightsHelpers.forEach((h) => {
                h.update()
            })
        } else {
            this.progShadowGrp.remove(this.debugHelpersGroup)
        }
    }


    // Implementation of MathUtils.randFloatSpread using MathUtils.seededRandom
    randFloatSpread(range, seed) {
        return (MathUtils.seededRandom(seed) - 0.5) * range
    }

    /**
     * Randomise direction lights
     */
    /**
     * Randomise direction lights
     */
    randomiseLights() {
        const length = this.lightOrigin.position.length()
        // Manually Update the Directional Lights
        for (let l = 0; l < this.dirLights.length; l++) {
            const seed = this.framesDone * 1000 + l * 20;
            // Sometimes they will be sampled from the target direction
            // Sometimes they will be uniformly sampled from the upper hemisphere
            if (MathUtils.seededRandom(seed) > this.params.ambientWeight) {
                this.dirLights[l].position.set(
                    this.lightOrigin.position.x + this.randFloatSpread(this.params.lightRadius, seed + 1),
                    this.lightOrigin.position.y + this.randFloatSpread(this.params.lightRadius, seed + 2),
                    this.lightOrigin.position.z + this.randFloatSpread(this.params.lightRadius, seed + 3)
                )
            } else {
                // Uniform Hemispherical Surface Distribution for Ambient Occlusion
                const lambda = Math.acos(2 * MathUtils.seededRandom(seed + 4) - 1) - 3.14159 / 2.0
                const phi = 2 * 3.14159 * MathUtils.seededRandom(seed + 5)
                this.dirLights[l].position.set(
                    Math.cos(lambda) * Math.cos(phi) * length,
                    Math.abs(Math.cos(lambda) * Math.sin(phi) * length),
                    Math.sin(lambda) * length
                )
            }

            if (this.params.debugHelpers) this.dirLightsHelpers[l].update()
        }
    }


    /**
     * Trigger an update
     */
    async recalculate() {
        if (!this.params.enabled) return
        this.clear()
        this.framesDone = 0
    }


    /**
     * Clear the shadow Target & update mesh list
     * Call this once after all the models are loaded
     */
    clear() {
        this.renderer.getClearColor(this.clearColor)
        this.clearAlpha = this.renderer.getClearAlpha()
        this.renderer.setClearColor("black", 1) // setting to any other color/alpha will decrease shadow's impact when accumulating
        this.renderer.setRenderTarget(this.progressiveLightMap1)
        this.renderer.clear()
        this.renderer.setRenderTarget(this.progressiveLightMap2)
        this.renderer.clear()
        this.renderer.setRenderTarget(null)
        this.renderer.setClearColor(this.clearColor, this.clearAlpha)

        this.shadowCatcherMesh.material.alphaTest = 0.0

        this.updateShadowObjectsList()
    }

    /**
     * Update list of meshes which need to be hidden
     */
    updateShadowObjectsList() {
        this.lights.length = 0
        this.meshes.length = 0
        this.objectsToHide.length = 0
        this.scene.traverse((object) => {
            if (object.geometry && object !== this.shadowCatcherMesh) {
                if (object.castShadow) {
                    this.meshes.push({ object, material: object.material })
                } else {
                    this.objectsToHide.push({ object, visible: object.visible })
                }
            } else if (object.isTransformControls) {
                this.objectsToHide.push({ object, visible: object.visible })
            } else if (object.isLight && object.parent !== this.lightGroup) {
                this.lights.push({ object, intensity: object.intensity })
            }
        })

        // console.log({ meshes: this.meshes, lights: this.lights, objectsToHide: this.objectsToHide })
    }

    /**
     * Add this function to animate loop
     * @param {Camera} camera
     */
    update(camera, limit = null) {
        if (!this.params.enabled || this.framesDone >= this.params.frames) return;

        // Prepare all meshes/lights
        this.lights.forEach((l) => (l.object.intensity = 0))
        this.meshes.forEach((m) => (m.object.material = this.discardMaterial))
        this.objectsToHide.forEach((m) => (m.object.visible = false))

        this.lightGroup.visible = true;
        let count = 0;

        while (this.framesDone < this.params.frames && (!limit || count < limit)) {
            this.shadowCatcherMesh.material.alphaTest = MathUtils.clamp(
                MathUtils.mapLinear(this.framesDone, 2, this.params.frames - 1, 0, this.params.alphaTest),
                0,
                1
            )

            this.randomiseLights()
            this.shadowCatcherMesh.material = this.targetMat;
            this.renderOnRenderTargets(camera)
            this.shadowCatcherMesh.material = this.shadowCatcherMaterial;

            this.progress = MathUtils.mapLinear(this.framesDone, 0, this.params.frames - 1, 0, 100)

            this.framesDone++;
            count++;
        }

        this.renderer.setRenderTarget(null);

        this.lights.forEach((l) => (l.object.intensity = l.intensity))
        this.meshes.forEach((m) => (m.object.material = m.material))
        this.objectsToHide.forEach((m) => (m.object.visible = m.visible))
        this.lightGroup.visible = false

        if (this.params.debugHelpers) this.showDebugHelpers(true)
    }
}

/**
 * r3f shader material which makes editing uniforms easy
 * @param {Object} uniforms
 * @param {String} vertexShader
 * @param {String} fragmentShader
 * @param {Function} onInit
 * @returns
 */
function shaderMaterial(uniforms = {}, vertexShader, fragmentShader, onInit = (material) => { }) {
    const material = class extends ShaderMaterial {
        constructor(parameters = {}) {
            const entries = Object.entries(uniforms)
            // Create uniforms and shaders
            super({
                uniforms: entries.reduce((acc, [name, value]) => {
                    const uniform = UniformsUtils.clone({ [name]: { value } })
                    return {
                        ...acc,
                        ...uniform,
                    }
                }, {}),
                vertexShader,
                fragmentShader,
            })
            // Create getter/setters
            entries.forEach(([name]) =>
                Object.defineProperty(this, name, {
                    get: () => this.uniforms[name].value,
                    set: (v) => (this.uniforms[name].value = v),
                })
            )

            // Assign parameters, this might include uniforms
            Object.assign(this, parameters)
            // Call onInit
            if (onInit) onInit(this)
        }
    }
    material.key = MathUtils.generateUUID()
    return material
}

/**
 * r3f shadow material
 */
const SoftShadowMaterial = shaderMaterial(
    {
        transparent: true,
        color: new Color(0, 0, 0),
        alphaTest: 0.0,
        opacity: 1.0,
        map: null,
        depthWrite: false,
        toneMapped: false,
        blend: 2.0,
    },
    `varying vec2 vUv;
    void main() {
    gl_Position = projectionMatrix * viewMatrix * modelMatrix * vec4(position, 1.);
    vUv = uv;
    }`,
    `varying vec2 vUv;
    uniform sampler2D map;
    uniform vec3 color;
    uniform float opacity;
    uniform float alphaTest;
    uniform float blend;
    void main() {
    vec4 sampledDiffuseColor = texture2D(map, vUv);
    gl_FragColor = vec4(color * sampledDiffuseColor.r * blend, max(0.0, (1.0 - (sampledDiffuseColor.r + sampledDiffuseColor.g + sampledDiffuseColor.b) / alphaTest)) * opacity);
    #include <tonemapping_fragment>
    #include <encodings_fragment>
    }`
)

/**
 * r3f Discard material which helps ignore materials when rendering
 */
const DiscardMaterial = shaderMaterial({}, "void main() { }", "void main() { gl_FragColor = vec4(0.0, 0.0, 0.0, 0.0); discard;  }")


import {
    DepthFormat, DepthTexture, LinearFilter, Matrix4, MeshStandardMaterial,
    PerspectiveCamera, UnsignedShortType, Vector4, KawaseBlurPass
  } from './three.bundle.js'
  
  export default class MeshReflectorMaterial extends MeshStandardMaterial {
    constructor(renderer, camera, scene, object, {
      mixBlur = 0,
      mixStrength = 1,
      resolution = 256,
      blur = [0, 0],
      minDepthThreshold = 0.9,
      maxDepthThreshold = 1,
      depthScale = 0,
      depthToBlurRatioBias = 0.25,
      mirror = 0,
      distortion = 1,
      mixContrast = 1,
      distortionMap,
      reflectorOffset = 0,
      bufferSamples = 8,
      planeNormal = new Vector3(0, 0, 1)
    } = {}, baseProps = {}) {
      super(baseProps);
  
      this.gl = renderer
      this.camera = camera
      this.scene = scene
      this.parent = object
  
      this.hasBlur = blur[0] + blur[1] > 0
      this.reflectorPlane = new Plane()
      this.normal = new Vector3()
      this.reflectorWorldPosition = new Vector3()
      this.cameraWorldPosition = new Vector3()
      this.rotationMatrix = new Matrix4()
      this.lookAtPosition = new Vector3(0, -1, 0)
      this.clipPlane = new Vector4()
      this.view = new Vector3()
      this.target = new Vector3()
      this.q = new Vector4()
      this.textureMatrix = new Matrix4()
      this.virtualCamera = new PerspectiveCamera()
      this.reflectorOffset = reflectorOffset;
      this.planeNormal = planeNormal
  
      this.setupBuffers(resolution, blur, bufferSamples);
  
      this.reflectorProps = {
        mirror,
        textureMatrix: this.textureMatrix,
        mixBlur,
        tDiffuse: this.fbo1.texture,
        tDepth: this.fbo1.depthTexture,
        tDiffuseBlur: this.fbo2.texture,
        hasBlur: this.hasBlur,
        mixStrength,
        minDepthThreshold,
        maxDepthThreshold,
        depthScale,
        depthToBlurRatioBias,
        distortion,
        distortionMap,
        mixContrast,
        'defines-USE_BLUR': this.hasBlur ? '' : undefined,
        'defines-USE_DEPTH': depthScale > 0 ? '' : undefined,
        'defines-USE_DISTORTION': distortionMap ? '' : undefined,
      }
    }
  
    setupBuffers(resolution, blur, bufferSamples) {
      const parameters = {
        minFilter: LinearFilter,
        magFilter: LinearFilter,
        encoding: this.gl.outputEncoding,
      }
  
      const fbo1 = new WebGLRenderTarget(resolution, resolution, parameters)
      fbo1.depthBuffer = true
      fbo1.depthTexture = new DepthTexture(resolution, resolution)
      fbo1.depthTexture.format = DepthFormat
      fbo1.depthTexture.type = UnsignedShortType
  
      const fbo2 = new WebGLRenderTarget(resolution, resolution, parameters)
  
      if (this.gl.capabilities.isWebGL2) {
        fbo1.samples = bufferSamples
      }
  
      this.fbo1 = fbo1;
      this.fbo2 = fbo2;
  
      this.kawaseBlurPass = new KawaseBlurPass()
      this.kawaseBlurPass.setSize(blur[0], blur[1])
    }
  
    beforeRender() {
      if (!this.parent) return
  
      this.reflectorWorldPosition.setFromMatrixPosition(this.parent.matrixWorld)
      this.cameraWorldPosition.setFromMatrixPosition(this.camera.matrixWorld)
      this.rotationMatrix.extractRotation(this.parent.matrixWorld)
  
      // was changed from this.normal.set(0, 0, 1)
      this.normal.copy(this.planeNormal)
      this.normal.applyMatrix4(this.rotationMatrix)
      this.reflectorWorldPosition.addScaledVector(this.normal, this.reflectorOffset)
      this.view.subVectors(this.reflectorWorldPosition, this.cameraWorldPosition)
      // Avoid rendering when reflector is facing away
      if (this.view.dot(this.normal) > 0) return
      this.view.reflect(this.normal).negate()
      this.view.add(this.reflectorWorldPosition)
      this.rotationMatrix.extractRotation(this.camera.matrixWorld)
      this.lookAtPosition.set(0, 0, -1)
      this.lookAtPosition.applyMatrix4(this.rotationMatrix)
      this.lookAtPosition.add(this.cameraWorldPosition)
      this.target.subVectors(this.reflectorWorldPosition, this.lookAtPosition)
      this.target.reflect(this.normal).negate()
      this.target.add(this.reflectorWorldPosition)
      this.virtualCamera.position.copy(this.view)
      this.virtualCamera.up.set(0, 1, 0)
      this.virtualCamera.up.applyMatrix4(this.rotationMatrix)
      this.virtualCamera.up.reflect(this.normal)
      this.virtualCamera.lookAt(this.target)
      this.virtualCamera.far = this.camera.far // Used in WebGLBackground
      this.virtualCamera.updateMatrixWorld()
      this.virtualCamera.projectionMatrix.copy(this.camera.projectionMatrix)
  
      // Update the texture matrix
      this.textureMatrix.set(0.5, 0.0, 0.0, 0.5, 0.0, 0.5, 0.0, 0.5, 0.0, 0.0, 0.5, 0.5, 0.0, 0.0, 0.0, 1.0)
      this.textureMatrix.multiply(this.virtualCamera.projectionMatrix)
      this.textureMatrix.multiply(this.virtualCamera.matrixWorldInverse)
      this.textureMatrix.multiply(this.parent.matrixWorld)
  
      // Now update projection matrix with new clip plane, implementing code from: http://www.terathon.com/code/oblique.html
      // Paper explaining this technique: http://www.terathon.com/lengyel/Lengyel-Oblique.pdf
      this.reflectorPlane.setFromNormalAndCoplanarPoint(this.normal, this.reflectorWorldPosition)
      this.reflectorPlane.applyMatrix4(this.virtualCamera.matrixWorldInverse)
      this.clipPlane.set(this.reflectorPlane.normal.x, this.reflectorPlane.normal.y, this.reflectorPlane.normal.z, this.reflectorPlane.constant)
      const projectionMatrix = this.virtualCamera.projectionMatrix
      this.q.x = (Math.sign(this.clipPlane.x) + projectionMatrix.elements[8]) / projectionMatrix.elements[0]
      this.q.y = (Math.sign(this.clipPlane.y) + projectionMatrix.elements[9]) / projectionMatrix.elements[5]
      this.q.z = -1.0
      this.q.w = (1.0 + projectionMatrix.elements[10]) / projectionMatrix.elements[14]
      // Calculate the scaled plane vector
      this.clipPlane.multiplyScalar(2.0 / this.clipPlane.dot(this.q))
  
      // Replacing the third row of the projection matrix
      projectionMatrix.elements[2] = this.clipPlane.x
      projectionMatrix.elements[6] = this.clipPlane.y
      projectionMatrix.elements[10] = this.clipPlane.z + 1.0
      projectionMatrix.elements[14] = this.clipPlane.w
    }
  
    update() {
      if (this.parent.material !== this) return;
  
      this.parent.visible = false
      const currentXrEnabled = this.gl.xr.enabled
      const currentShadowAutoUpdate = this.gl.shadowMap.autoUpdate
  
      this.beforeRender()
      this.gl.xr.enabled = false
      this.gl.shadowMap.autoUpdate = false
      this.gl.setRenderTarget(this.fbo1)
      this.gl.state.buffers.depth.setMask(true)
      if (!this.gl.autoClear) this.gl.clear()
  
      this.gl.render(this.scene, this.virtualCamera)
  
      if (this.hasBlur) {
        this.kawaseBlurPass.render(this.gl, this.fbo1, this.fbo2);
      }
  
      this.gl.xr.enabled = currentXrEnabled
      this.gl.shadowMap.autoUpdate = currentShadowAutoUpdate
      this.parent.visible = true
      this.gl.setRenderTarget(null)
    }
  
    onBeforeCompile(shader, ...args) {
      super.onBeforeCompile(shader, ...args);
  
      if (this.defines === undefined) this.defines = {}
  
      if (!this.defines.USE_UV) {
        this.defines.USE_UV = ''
      }
  
      if (this.reflectorProps["defines-USE_BLUR"] !== undefined) this.defines.USE_BLUR = ""
      if (this.reflectorProps["defines-USE_DEPTH"] !== undefined) this.defines.USE_DEPTH = ""
      if (this.reflectorProps["defines-USE_DISTORTION"] !== undefined) this.defines.USE_DISTORTION = ""
  
      let props = this.reflectorProps;
  
      for (let prop in props) {
        shader.uniforms[prop] = {
          get value() {
            return props[prop]
          }
        }
      }
  
      shader.vertexShader = `
              uniform mat4 textureMatrix;
              varying vec4 my_vUv;     
            ${shader.vertexShader}`
  
      shader.vertexShader = shader.vertexShader.replace(
        '#include <project_vertex>',
            /* glsl */`
            #include <project_vertex>
            my_vUv = textureMatrix * vec4( position, 1.0 );
            gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );
            `
      )
  
      shader.fragmentShader = /* glsl */`
              uniform sampler2D tDiffuse;
              uniform sampler2D tDiffuseBlur;
              uniform sampler2D tDepth;
              uniform sampler2D distortionMap;
              uniform float distortion;
              uniform float cameraNear;
              uniform float cameraFar;
              uniform bool hasBlur;
              uniform float mixBlur;
              uniform float mirror;
              uniform float mixStrength;
              uniform float minDepthThreshold;
              uniform float maxDepthThreshold;
              uniform float mixContrast;
              uniform float depthScale;
              uniform float depthToBlurRatioBias;
              varying vec4 my_vUv;        
              ${shader.fragmentShader}`
  
      shader.fragmentShader = shader.fragmentShader.replace(
        '#include <emissivemap_fragment>',
            /* glsl */`
            #include <emissivemap_fragment>
          
            float distortionFactor = 0.0;
            #ifdef USE_DISTORTION
              distortionFactor = texture2D(distortionMap, vUv).r * distortion;
            #endif
      
            vec4 new_vUv = my_vUv;
            new_vUv.x += distortionFactor;
            new_vUv.y += distortionFactor;
      
            vec4 base = texture2DProj(tDiffuse, new_vUv);
            vec4 blur = texture2DProj(tDiffuseBlur, new_vUv);
            
            vec4 merge = base;
            
            #ifdef USE_NORMALMAP
              vec2 normal_uv = vec2(0.0);
              vec4 normalColor = texture2D(normalMap, vUv);
              vec3 my_normal = normalize( vec3( normalColor.r * 2.0 - 1.0, normalColor.b,  normalColor.g * 2.0 - 1.0 ) );
              vec3 coord = new_vUv.xyz / new_vUv.w;
              normal_uv = coord.xy + coord.z * my_normal.xz * 0.05 * normalScale;
              vec4 base_normal = texture2D(tDiffuse, normal_uv);
              vec4 blur_normal = texture2D(tDiffuseBlur, normal_uv);
              merge = base_normal;
              blur = blur_normal;
            #endif
      
            float depthFactor = 0.0001;
            float blurFactor = 0.0;
      
            #ifdef USE_DEPTH
              vec4 depth = texture2DProj(tDepth, new_vUv);
              depthFactor = smoothstep(minDepthThreshold, maxDepthThreshold, 1.0-(depth.r * depth.a));
              depthFactor *= depthScale;
              depthFactor = max(0.0001, min(1.0, depthFactor));
      
              #ifdef USE_BLUR
                blur = blur * min(1.0, depthFactor + depthToBlurRatioBias);
                merge = merge * min(1.0, depthFactor + 0.5);
              #else
                merge = merge * depthFactor;
              #endif
        
            #endif
      
            float reflectorRoughnessFactor = roughness;
            #ifdef USE_ROUGHNESSMAP
              vec4 reflectorTexelRoughness = texture2D( roughnessMap, vUv );
              
              reflectorRoughnessFactor *= reflectorTexelRoughness.g;
            #endif
            
            #ifdef USE_BLUR
              blurFactor = min(1.0, mixBlur * reflectorRoughnessFactor);
              merge = mix(merge, blur, blurFactor);
            #endif
      
            vec4 newMerge = vec4(0.0, 0.0, 0.0, 1.0);
            newMerge.r = (merge.r - 0.5) * mixContrast + 0.5;
            newMerge.g = (merge.g - 0.5) * mixContrast + 0.5;
            newMerge.b = (merge.b - 0.5) * mixContrast + 0.5;
            
            diffuseColor.rgb = diffuseColor.rgb * ((1.0 - min(1.0, mirror)) + newMerge.rgb * mixStrength);
            `
      );
    }
  }